import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import AnnualMileageCalculator from '@acorn-portal/shared/src/components/calculators/AnnualMileageCalculator';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import useGetOrCreateMidTermAdjustment, {
	handleChangeOfUsageOrMilageRating,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { MtaChangeType, Product } from '@acorn-portal/shared/src/services/api';
import { setStartDate, updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { getLists } from '@acorn-portal/shared/src/store/referenceData.slice';
import { ANNUAL_MILEAGE, ANNUAL_MILEAGE_YD } from '@acorn-portal/shared/src/utils/modalText';
import { formatISO } from 'date-fns';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { IChangeUsageOrMileageForm } from './change-usage-or-mileage';
import { isTelematics } from '@acorn-portal/shared/src/utils';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';
import { Box } from '@mui/material';
import ChangeOfMileageSchema from '@acorn-portal/shared/src/utils/validation/ChangeOfMileageSchema';
import useGetMileageOptions from '../../hooks/useGetMileageOptions';
import { useMileageModalHelperText } from '../../hooks/useMileageModalHelperText';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IChangeMileageForm {
	annualMileage: string;
}

const ChangeOfMileage = () => {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [hasChanged] = useNoChangeWarning();
	const { track } = useTrack();

	useEffect(() => {
		track(`Top Up Miles Page Viewed`);
	}, []);
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const location = useLocation();

	const { annualMileageOptions } = useGetMileageOptions();

	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_USAGE_AND_MILEAGE,
		setAdjustmentLoading: setLoading,
	});
	const setMtaDate = (date: string) => {
		dispatch(setStartDate(date));
	};

	const setDefaultAdjustmentStartDate = () => {
		const now = new Date();
		const now30Mins = new Date(now.getTime() + 15 * 60000);
		setMtaDate(formatISO(now30Mins));
	};

	useEffect(() => {
		if (!mtaChanges.policyReference) {
			navigate(ROUTE_PATHS.home);
		}

		//if mileage policy, the MTA start date was already set on the TUM date page
		if (fullPolicy.product !== Product.TELEMATICS && fullPolicy.product !== Product.CARROT) {
			if (mtaChanges.isMtaFutureDated) {
				setDefaultAdjustmentStartDate();
			}
		}
	}, [location]);

	const fetchReferenceData = useCallback(async () => dispatch(getLists()), [dispatch]);

	useEffect(() => {
		if (annualMileageOptions.length === 0) {
			setLoading(true);
			fetchReferenceData().catch((e) => console.error('Error', e));
			setLoading(false);
		}
	}, [dispatch, fetchReferenceData, annualMileageOptions.length]);

	const { discardLoading } = useDiscardMta();

	const initialValues = {
		annualMileage: getClosestMileage(annualMileageOptions, midTermAdjustment.vehicleDetails.annualMileage),
	};

	const mileageModalHelperText = useMileageModalHelperText();

	const onSubmit = async (values: IChangeMileageForm): Promise<void> => {
		const newMTAChanges = values;
		const activePolicyValues = {
			annualMileage: getClosestMileage(annualMileageOptions, activePolicyDetails.vehicleDetails.annualMileage),
		};
		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, newMTAChanges),
			hasChanged(initialValues, newMTAChanges),
			mtaResponse,
			mtaChanges.changeOfUsage,
		);

		if (!continueToRate && !navigateToSummary) {
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}

		const command: IChangeUsageOrMileageForm = {
			vehicleUse: midTermAdjustment.vehicleDetails.vehicleUse,
			levelOfCover: midTermAdjustment.vehicleDetails.coverDefinitionCode as unknown as string,
			annualMileage: values.annualMileage,
			businessMileage: midTermAdjustment.vehicleDetails.businessMileage as unknown as string,
		};

		setLoading(true);
		try {
			const result = await handleChangeOfUsageOrMilageRating({
				midTermAdjustment,
				values: command,
				product: fullPolicy.product,
			});

			dispatch(
				updateMidTermAdjustmentDetails({
					...midTermAdjustment,
					changeOfUsage: updateSection,
					vehicleDetails: {
						...midTermAdjustment.vehicleDetails,
						annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
					},
				}),
			);
			if (!result) {
				throw new Error('Rating failed or no response received');
			}
			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}
			dispatch(setMidTermAdjustmentRatingResponse(result));
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error('Error during change of mileage submission:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='Top Up Miles' loading={loading || discardLoading}>
			<Formik<IChangeMileageForm>
				key={JSON.stringify(midTermAdjustment.transactionId)}
				validationSchema={ChangeOfMileageSchema(Number.parseInt(initialValues.annualMileage, 10))}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={() => navigate(-1)}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<SelectFormField
							label='Annual mileage'
							name='annualMileage'
							id='annualMileage'
							value={formik.values.annualMileage}
							onChange={(value) => {
								formik.setFieldValue('annualMileage', value);
							}}
							options={annualMileageOptions}
							modal={{
								title: ANNUAL_MILEAGE.header,
								description: !isTelematics(fullPolicy.product) ? ANNUAL_MILEAGE.text : ANNUAL_MILEAGE_YD.text,
							}}
							error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
							helperText={formik.touched.annualMileage && formik.errors.annualMileage}
						/>{' '}
						{isTelematics(fullPolicy.product) && (
							<Box
								sx={{
									position: 'relative',
									top: '-22px',
								}}
							>
								<AnnualMileageCalculator
									miles={Number.parseInt(formik.values.annualMileage, 10)}
									id='annualMileageCalculator'
									helperText={mileageModalHelperText}
								/>
							</Box>
						)}
						<ActionButtons>
							<BackButton onClick={() => setOpen(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								setOpen(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfMileage;
